<template>
  <div>
    <v-dialog v-model="inputDialog" persistent max-width="600px">
      <v-card :elevation="2" class="pa-3">
        <v-card-title class="headline">Upload File</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field v-model="plantName" readonly outlined required label="Plant Name"></v-text-field>
            <v-select
              v-model="selectedYear"
              :items="yearItems"
              label="Select Year"
              item-value="value"
              item-text="text"
              outlined
              required
            ></v-select>
            <v-text-field
              v-if="selectedYear"
              v-model="selectedFileName"
              label="Choose file"
              placeholder="Select a file"
              required
              outlined
              append-icon="mdi-paperclip"
              @click="triggerFileInput"
              @click:append="triggerFileInput"
              readonly
              class="file-input"
            ></v-text-field>
            <input
              v-if="selectedYear"
              type="file"
              ref="fileInput"
              @change="onFileChange"
              style="display: none"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" class="cancel-btn">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="previewDialog" persistent max-width="800px">
      <v-card>
        <v-card-title class="headline">File Preview</v-card-title>
        <v-card-text>
          <div class="spreadsheet-container" id="spreadsheetContainer"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="confirmDialog = true" class="upload-btn">Upload</v-btn>
          <v-btn text @click="cancelPreview" class="cancel-btn">Cancel</v-btn>
        </v-card-actions>
        <v-dialog v-model="confirmDialog" persistent max-width="290px">
          <v-card>
            <v-card-title class="headline">Confirm Upload</v-card-title>
            <v-card-text
              style="font-size: 17px"
            >Document once uploaded cannot be deleted. Are you sure you want to upload?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="cancel-btn" @click="confirmDialog = false">Cancel</v-btn>
              <v-btn class="upload-btn" @click="confirmUpload">Confirm</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>
    <v-layout row wrap justify-space-between>
      <v-flex xs12>
        <v-toolbar color="primary">
          <v-toolbar-title>Files</v-toolbar-title>
          <v-spacer></v-spacer>

          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" class="menu-button" title="File Options">
                File Options
                <v-icon>mdi-menu-down</v-icon>
              </v-btn>
            </template>

            <v-list class="menu-button">
              <v-list-tile @click="downloadTemplate">
                Download Template
                <v-icon right>mdi-download</v-icon>
              </v-list-tile>

              <v-list-tile @click="openInputHandler">
                Upload a File
                <v-icon right>mdi-upload</v-icon>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-toolbar>
        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          class="elevation-1 custom-table"
          hide-actions
          :loading="false"
        >
          <template v-slot:items="props">
            <tr>
              <td class="text-xs-left py-1 px-6" style="width: 20%">{{ props.item.uploadedBy }}</td>
              <td class="text-xs-center py-1 px-4" style="width: 25%">{{ props.item.uploadedAt }}</td>
              <td class="text-xs-center py-1 px-4" style="width: 20%">{{ props.item.year }}</td>
              <td class="text-xs-center py-1 px-4" style="width: 25%">{{ props.item.fileName }}</td>
              <td class="text-xs-center py-1 px-4" style="width: 10%">
                <v-btn
                  icon
                  @click="downloadFile(props.item)"
                  class="dow-file"
                  title="Download File"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </div>
</template>
    
  <script>
import { mapGetters, mapMutations } from "vuex";
import pvsystApi from "../services/pvsyst";
import ExcelJS from "exceljs";

export default {
  data() {
    return {
      inputDialog: false,
      previewDialog: false,
      confirmDialog: false,
      selectedYear: "",
      selectedFile: null,
      selectedFileName: "",
      formValid: false,
      workbook: null,
      sheetData: [],
      yearItems: [],
      tableHeaders: [
        {
          text: "Uploaded by",
          value: "uploadedBy",
          align: "left",
          sortable: false,
          width: "20%"
        },
        {
          text: "Uploaded on",
          value: "uploadedAt",
          align: "center",
          sortable: false,
          width: "25%"
        },
        {
          text: "Data for the year",
          value: "year",
          align: "center",
          sortable: false,
          width: "20%"
        },
        {
          text: "File Name",
          value: "fileName",
          align: "center",
          sortable: false,
          width: "25%"
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%"
        }
      ],
      tableData: []
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getPlantName"]),
    plantName() {
      return this.getPlantName(this.getSelectedPlantId);
    }
  },
  async mounted() {
    this.generateYearItems();
    await this.loadTableData();
  },
  methods: {
    ...mapMutations([
      "SET_PROGRESS_BAR",
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT"
    ]),
    updateSnackbar(payload) {
      this.SET_SHOW_SNACKBAR(true);
      this.SET_SNACKBAR_CONTENT(payload);
    },
    async loadTableData() {
      try {
        const response = await pvsystApi.loadTableData(this.getSelectedPlantId);
        const sortedData = response.data.data
          .map(row => ({
            year: row.year,
            uploadedBy: row.updatedby,
            uploadedAt: new Date(row.updtaedat),
            fileName: row.filename.split("/").pop()
          }))
          .sort((a, b) => b.uploadedAt - a.uploadedAt);
        this.tableData = sortedData.map(row => ({
          ...row,
          uploadedAt: row.uploadedAt.toLocaleString()
        }));
      } catch (error) {
        console.error("Error loading table data:", error);
      }
    },
    generateYearItems() {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 15; i++) {
        const year = currentYear - i;
        this.yearItems.push({ value: year, text: year.toString() });
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.loadFromFile(file);
        this.$nextTick(() => {
          this.previewDialog = true;
        });
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    closeDialog() {
      this.inputDialog = false;
      this.resetForm();
    },
    initSpreadsheet() {
      const spreadContainer = document.getElementById("spreadsheetContainer");
      spreadContainer.innerHTML = "<table id='spreadsheet'></table>";
    },
    loadFromFile(file) {
      const reader = new FileReader();
      reader.onload = async e => {
        const data = e.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        const worksheet = workbook.getWorksheet(1);
        const sheetData = [];
        worksheet.eachRow({ includeEmpty: true }, (row) => {
          const rowValues = [];
          row.eachCell({ includeEmpty: true }, (cell) => {
            let val = cell.value;
            if (val && typeof val === 'object' && val.result !== undefined) {
              val = val.result;
            } else if (val && typeof val === 'object' && val.text !== undefined) {
              val = val.text;
            }
            rowValues.push(val);
          });
          sheetData.push(rowValues);
        });
        this.renderToTable(sheetData);
      };
      reader.readAsArrayBuffer(file);
    },
    renderToTable(sheetData) {
      const table = document.getElementById("spreadsheet");
      table.innerHTML = "";
      sheetData.forEach(rowData => {
        const row = table.insertRow();
        rowData.forEach(cellData => {
          const cell = row.insertCell();
          cell.textContent = cellData;
        });
      });
    },
    async uploadFile() {
      try {
        // Create FormData object
        const formData = new FormData();
        formData.append("plantId", this.getSelectedPlantId);
        formData.append("year", this.selectedYear);
        formData.append("file", this.selectedFile);
        const headers = {
          "Content-Type": "multipart/form-data"
        };
        const response = await pvsystApi.submitForm(
          this.getSelectedPlantId,
          this.selectedYear,
          formData,
          headers
        );
        if (response.status === 200) {
          this.updateSnackbar({
            icon: "success",
            timeout: 5000,
            header: "",
            message: "File has been uploaded successfully!"
          });
          this.resetForm();
          this.inputDialog = false;
          this.previewDialog = false;
          await this.loadTableData();
        } else {
          this.updateSnackbar({
            icon: "error",
            timeout: 5000,
            header: "",
            message: "There was an error uploading the file. Please try again."
          });
        }
      } catch (error) {
        this.updateSnackbar({
          icon: "error",
          timeout: 5000,
          header: "",
          message: "Duplicate entry or invalid input."
        });
      }
    },
    confirmUpload() {
      this.confirmDialog = false;
      this.previewDialog = false;
      this.uploadFile();
    },
    openInputHandler() {
      this.inputDialog = true;
      this.initSpreadsheet();
    },
    cancelPreview() {
      this.previewDialog = false;
      this.resetForm();
    },
    resetForm() {
      this.selectedYear = "";
      this.selectedFile = null;
      this.selectedFileName = "";
      this.formValid = false;
    },
    async downloadFile(row) {
      const fileName = row.fileName;
      try {
        const response = await pvsystApi.getPresignedUrl(
          this.getSelectedPlantId,
          fileName
        );
        if (response && response.data && response.data.url) {
          const presignedUrl = response.data.url;
          window.open(presignedUrl);
        } else {
          throw new Error("No URL returned from the server");
        }
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
      }
    },
  async downloadTemplate() {
    try {
      const response = await pvsystApi.downloadTemplate();
      if (response && response.data && response.data.url) {
        const presignedUrl = response.data.url;
        window.open(presignedUrl, '_blank');
      } else {
        throw new Error("No URL returned from the server");
      }
      } catch (error) {
        this.updateSnackbar({
          icon: "error",
          timeout: 5000,
          header: "",
          message: "Failed to download template"
        });
      }
   }
  },
  watch: {
    getSelectedPlantId() {
      this.loadTableData();
    }
  }
};
</script>

  <style scoped>
.v-dialog .v-card,
.v-dialog .v-card-title,
.v-dialog .v-card-actions {
  background-color: var(--v-primary-base) !important;
  color: var(--primarytext);
}

.file-input {
  width: 100%;
}

.theme--dark.v-btn.cancel-btn {
  background-color: #ff6b7e;
  color: white;
  margin-bottom: 10px;
  margin-right: 10px;
}

.theme--dark.v-btn.upload-btn {
  background-color: #4caf50;
  color: white;
  margin-bottom: 10px;
}

.spreadsheet-container {
  width: 100%;
  height: 500px;
  margin-top: 20px;
}

.headline {
  margin-left: 30px;
}

.v-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 30px;
}
</style>
 
  <style>
.custom-table thead th:first-child {
  padding-left: 24px !important;
}

.custom-table thead tr:first-child {
  height: 54px !important;
}

.custom-data-table th {
  z-index: 1;
  position: sticky;
  top: 0;
}
.menu-button {
  background-color: var(--v-primary-base) !important;
  color: var(--primarytext);
}
</style>